<template>
  <div class="internal-body" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar />

    <!-- Hero -->
    <div class="px-3 px-md-4 referral-hero-wrap">
      <div class="referral-hero-panel rounded-5" :style="heroBgStyle">
        <div class="referral-hero-layers" aria-hidden="true"></div>
        <div class="referral-hero-edge-glow" aria-hidden="true"></div>
        <div class="container py-5 position-relative referral-hero-content">
          <div class="row referral-hero-row">
            <div class="col-12 text-center">
              <h1 class="text-white fw-bold mb-3 referral-hero-title">{{ t('referralPageTitle') }}</h1>
              <p class="text-white referral-hero-subtitle mb-4">{{ t('referralPageExplanation') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Link card -->
    <div id="referral-link-section" class="container-fluid position-relative overflow-hidden">
      <div>
        <img
          alt=""
          class="bg-circles"
          src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fcircles-background.png?alt=media&token=d420088e-66c6-4c95-bd3b-2f218dd36aba"
        />
      </div>
      <div class="container py-5">
        <div class="row justify-content-center">
          <div class="col-12 col-lg-8">
            <div class="card border-0 rounded-5 shadow-sm">
              <div class="card-body p-4 p-md-5 d-flex flex-column gap-4">

                <!-- Step label -->
                <div class="referral-step-label">
                  <span class="referral-step-badge">1</span>
                  <span class="referral-step-text">{{ t('referralStepCopyTitle') }}</span>
                </div>

                <!-- URL input + copy -->
                <div class="input-group referral-input-group" dir="ltr">
                  <textarea
                    ref="referralInput"
                    class="form-control referral-link-input"
                    :value="referralLink"
                    readonly
                    rows="1"
                    :aria-label="t('referralPageLinkLabel')"
                    @focus="selectReferralLink"
                    @click="selectReferralLink"
                  />
                  <button
                    class="btn referral-copy-btn"
                    :class="{ 'referral-copy-btn--copied': copyStatus === 'success' }"
                    type="button"
                    :aria-label="t('referralPageCopyBtn')"
                    @click="copyLink"
                  >
                    <span v-if="copyStatus === 'success'">✓ {{ t('referralPageCopied') }}</span>
                    <span v-else>{{ t('referralPageCopyBtn') }}</span>
                  </button>
                </div>

                <p
                  v-if="copyStatus"
                  class="referral-copy-feedback mb-0"
                  :class="{
                    'referral-copy-feedback--success': copyStatus === 'success',
                    'referral-copy-feedback--error': copyStatus === 'error',
                  }"
                  role="status"
                  aria-live="polite"
                >
                  {{ copyStatus === 'success' ? t('referralCopySuccessText') : t('referralCopyErrorText') }}
                </p>

                <p class="referral-helper-text mb-0">
                  {{ t('referralPageHelperText') }}
                </p>
                <p class="referral-helper-text referral-helper-text--highlight mb-0">
                  {{ t('referralRewardText') }}
                </p>

                <div class="referral-step-label">
                  <span class="referral-step-badge">2</span>
                  <span class="referral-step-text">{{ t('referralStepShareTitle') }}</span>
                </div>

                <!-- Share buttons -->
                <div class="referral-share-row">
                  <span class="referral-share-label">{{ t('referralShareVia') }}</span>
                  <div class="referral-share-btns">
                    <a
                      :href="whatsappShareUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn referral-share-btn referral-share-btn--wa"
                      :aria-label="t('referralShareWhatsapp')"
                    >
                      <svg class="referral-share-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {{ t('referralShareWhatsapp') }}
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PageFooterNew />
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import PageFooterNew from "@/components/PageFooterNew.vue";
import { mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: "ReferralPage",
  mixins: [translationMixin],
  components: { NavBar, PageFooterNew },
  data() {
    return {
      copyStatus: '',
      copyFeedbackTimeout: null,
      previousRootFontSize: '',
      heroBgStyle: {
        '--rm-bg': `url('https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/gui_images%2Ftravel-lake-forest-mountains-from-water.jpeg?alt=media&token=36ed69bc-4e96-46e3-b0aa-97f1ef21cbb6')`,
      },
    };
  },
  computed: {
    ...mapGetters(["isAppLoggedIn", "isUserManager", "userId"]),
    referralLink() {
      return `${window.location.origin}/welcome-ride-managers?ref=${this.userId}`;
    },
    whatsappShareText() {
      return this.t('referralShareWhatsappMessage', { link: this.referralLink });
    },
    whatsappShareUrl() {
      return `https://wa.me/?text=${encodeURIComponent(this.whatsappShareText)}`;
    },
  },
  methods: {
    selectReferralLink() {
      const input = this.$refs.referralInput;
      if (!input) return;
      input.focus();
      input.select();
    },
    setCopyStatus(status) {
      this.copyStatus = status;
      if (this.copyFeedbackTimeout) {
        clearTimeout(this.copyFeedbackTimeout);
      }
      this.copyFeedbackTimeout = setTimeout(() => {
        this.copyStatus = '';
      }, 4200);
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.referralLink);
        this.setCopyStatus('success');
      } catch {
        try {
          const input = this.$refs.referralInput;
          if (!input) throw new Error('Missing referral input');
          input.focus();
          input.select();
          const copied = document.execCommand('copy');
          this.setCopyStatus(copied ? 'success' : 'error');
        } catch {
          this.setCopyStatus('error');
        }
      }
    },
    scrollToLink() {
      const el = document.getElementById('referral-link-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
  },
  created() {
    if (!this.isAppLoggedIn || !this.isUserManager) {
      this.$router.push('/');
    }
  },
  mounted() {
    const rootElement = document.documentElement;
    this.previousRootFontSize = rootElement.style.fontSize || '';
    rootElement.style.fontSize = '16px';
  },
  beforeDestroy() {
    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
    }
    document.documentElement.style.fontSize = this.previousRootFontSize;
  },
};
</script>

<style scoped>
.internal-body {
  line-height: 1.5;
}

/* ── Hero ─────────────────────────────────────────────── */
.referral-hero-wrap {
  padding-top: 98px;
  padding-bottom: 1rem;
}

.referral-hero-panel {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background-image:
    linear-gradient(135deg, rgba(18,22,28,0.72) 0%, rgba(28,32,38,0.58) 48%, rgba(12,14,18,0.75) 100%),
    var(--rm-bg);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  min-height: 320px;
  box-shadow:
    0 28px 56px -12px rgba(0,0,0,0.4),
    0 0 0 1px rgba(255,255,255,0.1) inset;
}

.referral-hero-layers {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background:
    radial-gradient(ellipse 100% 70% at 50% -25%, rgba(255,255,255,0.14), transparent 55%),
    linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.38) 100%);
  pointer-events: none;
  z-index: 0;
}

.referral-hero-edge-glow {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.22);
  pointer-events: none;
  z-index: 1;
}

.referral-hero-content { z-index: 2; }

.referral-hero-row {
  animation: referral-enter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes referral-enter {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}

.referral-hero-title {
  font-size: clamp(2.65rem, 3.15vw, 3.85rem);
  line-height: 1.12;
  letter-spacing: -0.035em;
  font-weight: 800;
  text-shadow: 0 2px 24px rgba(0,0,0,0.28), 0 0 60px rgba(255,255,255,0.06);
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
}

.referral-hero-subtitle {
  font-size: clamp(1.3rem, 2.05vw, 1.7rem);
  line-height: 1.55;
  opacity: 0.94;
  max-width: 44rem;
  margin-left: auto;
  margin-right: auto;
}

.referral-hero-cta {
  position: relative;
  overflow: hidden;
  border: none;
  font-size: clamp(1.18rem, 1.55vw, 1.38rem);
  background: linear-gradient(135deg, #42dcc0 0%, #34d0b6 35%, #0a9ec4 100%);
  box-shadow:
    0 10px 28px rgba(52,208,182,0.42),
    0 0 0 1px rgba(255,255,255,0.25) inset;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.referral-hero-cta:hover {
  color: #fff;
  transform: translateY(-3px) scale(1.01);
  box-shadow:
    0 16px 40px rgba(52,208,182,0.5),
    0 0 0 1px rgba(255,255,255,0.35) inset;
}

.referral-cta-shine {
  position: absolute;
  top: 0;
  left: -40%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
  transform: skewX(-18deg);
  animation: referral-shine 4.5s ease-in-out infinite;
  pointer-events: none;
}

@keyframes referral-shine {
  0%, 100% { left: -50%; opacity: 0; }
  15%       { opacity: 0.5; }
  35%       { left: 120%; opacity: 0; }
}

.referral-cta-label {
  position: relative;
  z-index: 1;
}

/* ── Link card ─────────────────────────────────────────── */
.bg-circles {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  max-width: none;
  z-index: -1;
}

.rounded-5 {
  border-radius: 32px !important;
}

/* Step label */
.referral-step-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
}

.referral-step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  background: linear-gradient(145deg, #42dcc0, #089ec4);
  color: #fff;
  font-weight: 800;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(52,208,182,0.45);
}

.referral-step-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e1e1e;
}

/* URL input */
.referral-input-group {
  border-radius: 12px;
  overflow: hidden;
}

.referral-link-input {
  font-size: 1.05rem;
  background-color: #f8f9fa;
  padding: 0.85rem 1rem;
  border: 1.5px solid #dee2e6;
  border-inline-end: none;
  line-height: 1.35;
  resize: none;
  width: 1%;
  flex: 1 1 auto;
  white-space: normal;
  overflow-wrap: anywhere;
  min-height: 46px;
}

.referral-copy-btn {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  border: 0;
  font-size: 1rem;
  padding: 0 1.25rem;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.15s ease;
  white-space: nowrap;
}

.referral-copy-btn:hover {
  background: linear-gradient(45deg, #39e1c5, #08add3);
  color: #fff;
  transform: scale(1.02);
}

.referral-copy-btn--copied {
  background: linear-gradient(45deg, #28a745, #20c85a) !important;
}

.referral-copy-feedback {
  font-size: 0.95rem;
  font-weight: 600;
}

.referral-copy-feedback--success {
  color: #1f8f46;
}

.referral-copy-feedback--error {
  color: #c13d3d;
}

.referral-helper-text {
  font-size: 0.98rem;
  color: rgba(33,37,41,0.75);
  margin-top: 0.35rem;
  margin-bottom: 0.5rem !important;
  text-align: right;
}

.referral-helper-text--highlight {
  color: #0a7e96;
  font-weight: 600;
}

.internal-body[dir='ltr'] .referral-helper-text {
  text-align: left;
}

/* Share buttons */
.referral-share-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 0.35rem;
}

.referral-share-label {
  font-size: 1rem;
  color: rgba(33,37,41,0.65);
  white-space: nowrap;
}

.referral-share-btns {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.referral-share-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  min-height: 46px;
  border-radius: 999px;
  color: #fff;
  text-decoration: none;
  border: 0;
  transition: opacity 0.2s ease, transform 0.15s ease;
}

.referral-share-btn:hover {
  color: #fff;
  opacity: 0.9;
  transform: translateY(-1px);
  text-decoration: none;
}

.referral-share-btn--wa  { background: #25D366; }
.referral-share-btn--fb  { background: #1877F2; }

.referral-share-icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}

/* ── Mobile ────────────────────────────────────────────── */
@media (max-width: 575.98px) {
  .referral-hero-wrap {
    padding-top: 74px;
  }
  .referral-hero-title {
    font-size: clamp(2.2rem, 6vw, 2.8rem);
  }
  .referral-input-group {
    flex-direction: column;
    gap: 0.65rem;
    border-radius: 0;
    overflow: visible;
  }
  .referral-link-input {
    border-inline-end: 1.5px solid #dee2e6;
    border-radius: 12px !important;
    font-size: 16px;
    line-height: 1.45;
    min-height: 88px;
    width: 100% !important;
    flex: 1 1 auto;
  }
  .referral-copy-btn {
    width: 100%;
    border-radius: 12px;
    min-height: 46px;
  }
  .referral-share-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .referral-share-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .referral-hero-row { animation: none; }
  .referral-cta-shine { animation: none; }
  .referral-hero-cta:hover { transform: none; }
}
</style>
